import {
  useFormattedSites,
  useGetPeriodOptions,
} from '@app/ccf/company-report/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAccount, useGetSites } from '@shared/api';
import { useGetOrganizationTimePeriods } from '@shared/api/hooks/ccf/periods';
import {
  useDeletePurchasedPackagingItemRecord,
  usePatchPurchasedPackagingItemRecord,
} from '@shared/api/hooks/purchased-goods/packaging';
import { PackagingActivityRecord } from '@shared/api/types';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import SelectSearchFormField from '@shared/components/form/select-search-form-field';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { Skeleton } from '@shared/components/ui/skeleton';
import { useToast } from '@shared/components/ui/use-toast';
import { getCurrencySymbol } from '@shared/lib/format';
import sentry from '@shared/services/sentry';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { object, string, z } from 'zod';
import { usePurchasedPackagingItem } from '../../purchased-packaging-item-context';

interface PurchasedPackagingItemPurchasesEditFormProps {
  packagingActivityRecord?: PackagingActivityRecord;
  onOpenChange?: (open: boolean) => void;
  numberOfPurchases: number;
}

export const packagingItemPurchasesSchema = object({
  uuid: string().min(1),
  site: string().optional(),
  totalQuantity: z.coerce.number().min(0.001),
  timePeriod: string().min(1),
  quantity: z.coerce.number().min(1),
  supplier: string().optional(),
  totalSpend: z.preprocess(
    (val) => (val === '' ? null : val),
    z.coerce.number().min(0).nullable().optional()
  ),
});

export default function PurchasedPackagingItemPurchasesEditForm({
  packagingActivityRecord,
  onOpenChange,
  numberOfPurchases,
}: PurchasedPackagingItemPurchasesEditFormProps) {
  const { toast } = useToast();
  const { item } = usePurchasedPackagingItem();
  const { data: account } = useAccount(true);

  const {
    mutateAsync: updatePackagingPurchase,
    isLoading: isUpdatingPackagingPurchase,
  } = usePatchPurchasedPackagingItemRecord();

  const {
    mutateAsync: deletePackagingPurchase,
    isLoading: isDeletingPackagingPurchase,
  } = useDeletePurchasedPackagingItemRecord();

  const { data: sites, isLoading: isSitesLoading } =
    useGetSites({
      page: 1,
      page_size: 1000,
    }) || [];

  const formattedSites = useFormattedSites(sites?.results || []);

  const { data: periods, isLoading: isPeriodsLoading } =
    useGetOrganizationTimePeriods();
  const getPeriodOptions = useGetPeriodOptions(periods!);

  const form = useForm<z.infer<typeof packagingItemPurchasesSchema>>({
    resolver: zodResolver(packagingItemPurchasesSchema),
  });

  const { reset, formState, watch, setValue } = form;

  const onSubmit = async (
    data: z.infer<typeof packagingItemPurchasesSchema>
  ) => {
    const updateData = {
      timePeriod: data.timePeriod,
      quantity: data.quantity,
      uuid: data.uuid,
      totalQuantity: data.totalQuantity,
      site: data.site,
      totalSpend: data.totalSpend ?? null,
    };

    try {
      await updatePackagingPurchase({
        activityUuid: item.uuid,
        recordUuid: data.uuid,
        data: updateData,
      });

      onOpenChange?.(false);
      toast({
        title: 'Purchase updated',
        variant: 'success',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to update purchase',
        variant: 'destructive',
      });
    }
  };

  const onDelete = async () => {
    try {
      await deletePackagingPurchase({
        activityUuid: item.uuid,
        recordUuid: packagingActivityRecord!.uuid,
      });
      onOpenChange?.(false);
      toast({
        title: 'Purchase deleted',
        variant: 'success',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to delete purchase',
        variant: 'destructive',
      });
    }
  };

  const quantity = watch('quantity');
  const itemsPerPack = item.itemsPerPack;

  const setTotalQuantity = useMemo(() => {
    return (quantity: number, itemsPerPack: number) => {
      setValue('totalQuantity', quantity * itemsPerPack);
    };
  }, [setValue]);

  useEffect(() => {
    setTotalQuantity(quantity, itemsPerPack);
  }, [itemsPerPack, quantity, setTotalQuantity]);

  useEffect(() => {
    if (packagingActivityRecord) {
      reset({
        timePeriod: packagingActivityRecord.timePeriod.uuid,
        quantity: packagingActivityRecord.quantity,
        uuid: packagingActivityRecord.uuid,
        totalQuantity: Number(packagingActivityRecord.totalQuantity),
        site: packagingActivityRecord.site?.uuid || '',
        supplier: packagingActivityRecord.supplier?.name || '',
        totalSpend: packagingActivityRecord.totalSpend ?? undefined,
      });
    }
  }, [packagingActivityRecord, reset]);

  if (isPeriodsLoading || isSitesLoading) {
    return (
      <div className="grid grid-cols-12 items-end gap-2">
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
      </div>
    );
  }

  if (!periods) {
    throw new Error(
      `Failed to load periods for food activity rows ${isPeriodsLoading || 'No data'}`
    );
  }

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-12 items-start gap-3">
          <SelectFormField
            name="timePeriod"
            options={getPeriodOptions}
            disabled
            label="Period"
            className="col-span-4"
            loading={isPeriodsLoading}
          />
          <SelectSearchFormField
            name="site"
            pluralName="sites"
            options={formattedSites}
            label="Site"
            disabled={
              isUpdatingPackagingPurchase || isDeletingPackagingPurchase
            }
            className="col-span-4"
          />
          <InputFormField
            name="supplier"
            label="Supplier"
            disabled
            className="col-span-4"
          />
          <InputFormField
            name="quantity"
            label="Packs purchased"
            className="col-span-4"
            type="number"
            disabled={
              isUpdatingPackagingPurchase || isDeletingPackagingPurchase
            }
            inputClassName="text-right"
          />
          <InputFormField
            name="totalQuantity"
            label="Total quantity"
            className="col-span-4"
            disabled
            inputClassName="text-right"
          />
          <InputFormField
            name="totalSpend"
            label="Total spend"
            type="number"
            disabled
            className="col-span-4"
            inputClassName="text-right"
            adornmentRight={getCurrencySymbol(account?.defaultCurrency)}
            optional
          />
        </div>
        <div className="flex justify-between">
          <Button
            type="button"
            variant="destructive"
            onClick={() => onDelete()}
            disabled={isUpdatingPackagingPurchase || numberOfPurchases <= 1}
            loading={isUpdatingPackagingPurchase || isDeletingPackagingPurchase}
          >
            Delete purchase
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={isUpdatingPackagingPurchase}
              loading={
                isUpdatingPackagingPurchase || isDeletingPackagingPurchase
              }
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={
                isUpdatingPackagingPurchase || isDeletingPackagingPurchase
              }
              disabled={isUpdatingPackagingPurchase || !formState.isDirty}
            >
              {isUpdatingPackagingPurchase ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
